const absolute = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" });
const relative = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Largest unit first: the first threshold the gap clears is the one used. */
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["day", DAY],
  ["hour", HOUR],
  ["minute", MINUTE],
  ["second", 1],
];

/**
 * Read at UTC, so a date that is only a date is never moved a day by a
 * timezone. In en-GB, as `bucketLabel` writes the site's days: these are
 * dates on the site's calendar, not moments in the reader's.
 */
const calendar = new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeZone: "UTC" });

/** A calendar date the API sent as YYYY-MM-DD, such as the day a tariff starts. */
export function formatLocalDate(date: string): string {
  const at = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(at.getTime()) ? date : calendar.format(at);
}

export function formatDateTime(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : absolute.format(at);
}

/**
 * "3 hours ago", in the reader's own locale and timezone. Used alongside the
 * absolute time rather than instead of it: relative reads faster, absolute is
 * the one worth comparing against a memory of what you were doing.
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;

  const seconds = Math.round((at.getTime() - now) / 1000);
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size || unit === "second") {
      return relative.format(Math.trunc(seconds / size), unit);
    }
  }
  return absolute.format(at);
}

const decimal = new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 });
const amount = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const percent = new Intl.NumberFormat(undefined, { style: "percent", maximumFractionDigits: 0 });
const whole = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

/**
 * Presentation only. Every value shown here was produced by a service; nothing
 * on this side of the wire may compute one (docs/ARCHITECTURE.md section 2).
 */
export function formatUnit(value: number | null, unit: string, whole_ = false): string {
  if (value === null) return "—";
  return `${(whole_ ? whole : decimal).format(value)} ${unit}`;
}

/** A sum of money the API priced, to the kobo, without its currency. */
export function formatAmount(value: number | null): string {
  return value === null ? "—" : amount.format(value);
}

/** "1,234.50 NGN". The code after the figure, as `formatUnit` puts a unit. */
export function formatMoney(value: number | null, currency: string): string {
  return value === null ? "—" : `${amount.format(value)} ${currency}`;
}

/** A ratio the API worked out, such as 0.87, as "87%". */
export function formatPercent(ratio: number | null): string {
  return ratio === null ? "—" : percent.format(ratio);
}

/**
 * How old a reading is, in words -- or how far ahead of the server it is
 * dated, which is a different sentence and used to be printed as the first.
 *
 * formatDuration takes the magnitude, so a negative age read as a past one:
 * an inverter running an hour fast was shown as an hour stale rather than as
 * dated in the future. Nothing in the stack rejects such a reading -- the
 * server subtracts without a floor and the stale rule is a > comparison a
 * negative age can never satisfy -- so this is the one place a reader is told.
 */
export function formatAge(seconds: number): string {
  if (seconds < 0) return `${formatDuration(seconds)} ahead of the server's clock`;
  return `${formatDuration(seconds)} ago`;
}

/** "5 minutes", for an age already measured by the server. */
export function formatDuration(seconds: number): string {
  const parts: [number, string][] = [
    [86_400, "day"],
    [3_600, "hour"],
    [60, "minute"],
  ];
  const magnitude = Math.abs(seconds);
  for (const [size, unit] of parts) {
    if (magnitude >= size) {
      const count = Math.round(magnitude / size);
      return `${count} ${unit}${count === 1 ? "" : "s"}`;
    }
  }
  const count = Math.round(magnitude);
  return `${count} second${count === 1 ? "" : "s"}`;
}
